import { SidebarProvider, SidebarTrigger } from '@/components/ui/sidebar';
import AppSidebar from '@/components/AppSidebar';
import React from 'react';
import { Outlet } from 'react-router-dom';
import { SidebarCollapseProvider } from '@/contexts/SidebarCollapseContext';
import { Banner } from '@/components/ui/banner';
import { useBannerMessage } from '@/hooks/useBannerMessage';
import { useUser } from '@/providers/UserProvider';
import BeaconLauncher from '@/components/BeaconLauncher';
import OneVerseLogo from '@/components/OneVerseLogo';

interface AppLayoutProps {
  fullPage?: boolean;
}

const AppLayout: React.FC<AppLayoutProps> = ({ fullPage = false }) => {
  const { message, isLoading, dismissBanner } = useBannerMessage();
  const { userData } = useUser();
  const throttlesDisabled = userData?.api_throttles_disabled === true;

  return (
    <SidebarCollapseProvider>
      <SidebarProvider>
        {!fullPage && <AppSidebar />}

        <div className="flex-1 flex flex-col min-w-0">
          {!fullPage && (
            <header className="md:hidden flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-white sticky top-0 z-10">
              <OneVerseLogo className="w-28" isCollapsed={false} />
              <SidebarTrigger />
            </header>
          )}
          {/*
            Load-testing mode. Deliberately not dismissible and shown on every
            page including full-page views: it is the reminder that this
            environment currently has no API rate limiting, and a banner you can
            dismiss is one you stop noticing. It disappears on its own once the
            flag is off, so it can never be stale.
          */}
          {throttlesDisabled && (
            <div
              role="status"
              className="w-full bg-amber-500 px-4 py-2 text-center text-sm font-semibold text-white"
            >
              Load testing: API rate limiting is disabled in this environment.
              Turn it back on when the run is finished.
            </div>
          )}
          {!fullPage && !isLoading && message && (
            <Banner onDismiss={dismissBanner}>{message}</Banner>
          )}
          <main
            className="flex-1 overflow-x-hidden"
            style={{ backgroundColor: 'var(--color-app-bg)' }}
          >
            <Outlet />
          </main>
        </div>
        <BeaconLauncher />
      </SidebarProvider>
    </SidebarCollapseProvider>
  );
};

export default AppLayout;
